// AdminEditController — the admin radial-menu action dispatcher (map-architecture
// Phase 7, P7 "Admin radial-дії = string-dispatch замість Command").
//
// A plain TypeScript class (NOT React). Replaces the if/else-chain string-dispatch
// that used to live in `executeAdminRadialAction` (useMapAdminInteraction.ts) with
// a Command pattern: one Command object per radial action (toggle_mode / delete /
// mirror_h / rotate_180 / duplicate / default_size — the exact set that exists in
// getAdminRadialMenuOptions, nothing invented), looked up in a `Record<actionKey,
// AdminCommand>` table and dispatched via `execute()`.
//
// The bodies are moved VERBATIM from the original if/else branches; the only
// change is closure variable → context access (`this.ctx.foo` / destructured from
// it) and `mapObject`/`marker` → the command's own `execute(mapObject, marker)`
// parameters. Order of side-effects, await/async structure and error handling are
// unchanged.
//
// The trailing `closeAdminRadialMenu()` call that used to sit OUTSIDE the if/else
// chain (i.e. runs unconditionally after every dispatch, including for an
// unrecognized actionKey) stays outside the commands too — it lives in
// `AdminEditController.execute()`, not in any individual command. The DeleteCommand
// additionally has its OWN inner `closeAdminRadialMenu()` call inside the async
// confirm callback (a distinct call-site from the original code) — both are kept,
// verbatim, at their original call sites.
//
// useMapAdminInteraction.ts constructs a fresh AdminEditController on every hook
// call (i.e. every MapCanvas render), exactly mirroring how the original
// `executeAdminRadialAction` was itself just a fresh closure created on every hook
// call — no stale-closure risk, no behavior change.
import type { Dispatch, MutableRefObject, SetStateAction } from 'react';
import * as PIXI from 'pixi.js';
import type { QueryClient } from '@tanstack/react-query';
import { normalizeMapObject, MapObject } from '../../../../models/mapObject';
import {
  adminUpdateMapObjectCoordinatesAdminMapObjectsMapObjectIdCoordinatesPut,
  createPlotAdminPlotsPost,
  deleteMapObjectAdminMapObjectsMapObjectIdDelete,
  updateMapObjectAdminMapObjectsMapObjectIdPut,
} from '../../../../client';
import { showAlert, showConfirm } from '../../../../utils/telegram/alert';
import type { MapSceneState } from '../../mapSceneState';

export interface AdminEditControllerContext {
  /** Scene bag is read only inside command execute (event handlers), never during render. */
  sceneRef: MutableRefObject<MapSceneState>;
  setAdminResizeDims: Dispatch<SetStateAction<{ w: number; h: number } | null>>;
  closeAdminRadialMenu: () => void;
  scheduleDeleteGuardClear: (mapObjectId: number) => void;
  queryClient: QueryClient;
  REAL_ESTATE_FIXED_SIZES: Record<number, { width: number; height: number }>;
  PLOT_MARKER_SIZE: { width: number; height: number };
  STATIC_OBJECT_FIXED_SIZES: Record<string, { width: number; height: number }>;
}

interface AdminCommand {
  execute(mapObject: MapObject, marker: PIXI.Container): void;
}

// ── toggle_mode (verbatim body) ───────────────────────────────────────────────
class ToggleModeCommand implements AdminCommand {
  constructor(private ctx: AdminEditControllerContext) {}
  execute = (mapObject: MapObject): void => {
    const scene = this.ctx.sceneRef.current;
    const currentMode = scene.admin.objectModes.get(mapObject.id) ?? 'dnd';
    const newMode = currentMode === 'dnd' ? 'resize' : 'dnd';
    scene.admin.objectModes.set(mapObject.id, newMode);
  };
}

// ── delete (verbatim body) ────────────────────────────────────────────────────
class DeleteCommand implements AdminCommand {
  constructor(private ctx: AdminEditControllerContext) {}
  execute = (mapObject: MapObject): void => {
    const { sceneRef, closeAdminRadialMenu, queryClient, scheduleDeleteGuardClear } = this.ctx;
    const scene = sceneRef.current;
    if (!scene.data.deletingMapObjectIds.has(mapObject.id)) {
      showConfirm('Delete this object?').then(confirmed => {
        if (confirmed) {
          closeAdminRadialMenu();
          scene.data.deletingMapObjectIds.add(mapObject.id);
          queryClient.setQueryData<MapObject[]>(['mapObjects'], (oldData = []) =>
            oldData.filter(o => o.id !== mapObject.id)
          );
          deleteMapObjectAdminMapObjectsMapObjectIdDelete({
            path: { map_object_id: mapObject.id },
            throwOnError: true,
          })
            .catch(() => queryClient.invalidateQueries({ queryKey: ['mapObjects'] }))
            .finally(() => scheduleDeleteGuardClear(mapObject.id));
        }
      });
    }
  };
}

// ── mirror_h (verbatim body) ──────────────────────────────────────────────────
class MirrorHCommand implements AdminCommand {
  constructor(private ctx: AdminEditControllerContext) {}
  execute = (mapObject: MapObject, marker: PIXI.Container): void => {
    const { queryClient } = this.ctx;
    const sp = marker.children.find(c => c instanceof PIXI.Sprite) as PIXI.Sprite | undefined;
    if (sp && !sp.destroyed) {
      sp.scale.x = -sp.scale.x;
    }
    const currentMirrorH = mapObject.data?.mirror_h ?? false;
    const newData = { ...(mapObject.data as object || {}), mirror_h: !currentMirrorH };
    updateMapObjectAdminMapObjectsMapObjectIdPut({
      path: { map_object_id: mapObject.id },
      body: { data: newData },
      throwOnError: true,
    })
      .then(() => {
        queryClient.setQueryData<MapObject[]>(['mapObjects'], (old = []) =>
          old.map(o => o.id === mapObject.id ? { ...o, data: newData } : o));
      })
      .catch(() => showAlert('Помилка при відзеркаленні острова'));
  };
}

// ── rotate_180 (verbatim body) ────────────────────────────────────────────────
class Rotate180Command implements AdminCommand {
  constructor(private ctx: AdminEditControllerContext) {}
  execute = (mapObject: MapObject, marker: PIXI.Container): void => {
    const { queryClient } = this.ctx;
    // Same logic as mirror_h: simple scale.x toggle
    const sp = marker.children.find(c => c instanceof PIXI.Sprite) as PIXI.Sprite | undefined;
    if (sp && !sp.destroyed) {
      sp.scale.x = -sp.scale.x;
    }
    const currentMirrored = mapObject.data?.rotate_180 ?? false;
    const newData = { ...(mapObject.data as object || {}), rotate_180: !currentMirrored };
    updateMapObjectAdminMapObjectsMapObjectIdPut({
      path: { map_object_id: mapObject.id },
      body: { data: newData },
      throwOnError: true,
    })
      .then(() => {
        queryClient.setQueryData<MapObject[]>(['mapObjects'], (old = []) =>
          old.map(o => o.id === mapObject.id ? { ...o, data: newData } : o));
      })
      .catch(() => showAlert('Error mirroring object'));
  };
}

// ── duplicate (verbatim body) ─────────────────────────────────────────────────
class DuplicateCommand implements AdminCommand {
  constructor(private ctx: AdminEditControllerContext) {}
  execute = (mapObject: MapObject): void => {
    const { queryClient } = this.ctx;
    const offset = 50;
    createPlotAdminPlotsPost({
      body: {
        x: mapObject.x + offset,
        y: mapObject.y + offset,
        type: mapObject.type ?? undefined,
        data: mapObject.data ? { ...(mapObject.data as object) } : undefined,
        display_width: mapObject.display_width ?? undefined,
        display_height: mapObject.display_height ?? undefined,
      },
      throwOnError: true,
    })
      .then(({ data }) => {
        const newObj = normalizeMapObject(data);
        queryClient.setQueryData<MapObject[]>(['mapObjects'], (old = []) => [...old, newObj]);
      })
      .catch(() => showAlert('Error duplicating object'));
  };
}

// ── default_size (verbatim body) ──────────────────────────────────────────────
class DefaultSizeCommand implements AdminCommand {
  constructor(private ctx: AdminEditControllerContext) {}
  execute = (mapObject: MapObject, marker: PIXI.Container): void => {
    const { queryClient, setAdminResizeDims, REAL_ESTATE_FIXED_SIZES, PLOT_MARKER_SIZE, STATIC_OBJECT_FIXED_SIZES } = this.ctx;
    // Reset to the hardcoded design size — same priority the renderer uses
    // (REAL_ESTATE_FIXED_SIZES for houses, STATIC_OBJECT_FIXED_SIZES for static
    // objects, PLOT_MARKER_SIZE for plots). Native texture size only as a last
    // resort (decor_* objects render at natural size by design).
    const sp = marker.children.find(c => c instanceof PIXI.Sprite) as PIXI.Sprite | undefined;
    if (sp && !sp.destroyed && sp.texture) {
      let designSize: { width: number; height: number } | undefined;
      if (mapObject.type === 'house') {
        const rid = mapObject.data?.real_estate_id as number | undefined;
        designSize = rid ? REAL_ESTATE_FIXED_SIZES[rid] : undefined;
      } else if (mapObject.type === 'plot') {
        designSize = PLOT_MARKER_SIZE;
      } else if (!mapObject.type.startsWith('decor_')) {
        designSize = STATIC_OBJECT_FIXED_SIZES[mapObject.type];
      }
      const defaultW = Math.round(designSize?.width ?? sp.texture.width);
      const defaultH = Math.round(designSize?.height ?? sp.texture.height);
      // Show dimensions briefly (delayed so cleanup() doesn't wipe it)
      setTimeout(() => {
        setAdminResizeDims({ w: defaultW, h: defaultH });
        setTimeout(() => setAdminResizeDims(null), 2000);
      }, 50);
      sp.width = defaultW;
      sp.height = defaultH;
      adminUpdateMapObjectCoordinatesAdminMapObjectsMapObjectIdCoordinatesPut({
        path: { map_object_id: mapObject.id },
        body: {
          x: marker.x,
          y: marker.y,
          display_width: defaultW,
          display_height: defaultH,
        },
        throwOnError: true,
      }).then(() => {
        queryClient.setQueryData<MapObject[]>(['mapObjects'], (oldData = []) =>
          oldData.map(obj =>
            obj.id === mapObject.id
              ? { ...obj, display_width: defaultW, display_height: defaultH }
              : obj
          )
        );
      }).catch(() => {
        showAlert('Error resetting size');
      });
    }
  };
}

export class AdminEditController {
  private ctx: AdminEditControllerContext;
  private commands: Record<string, AdminCommand>;

  constructor(ctx: AdminEditControllerContext) {
    this.ctx = ctx;
    this.commands = {
      toggle_mode: new ToggleModeCommand(ctx),
      delete: new DeleteCommand(ctx),
      mirror_h: new MirrorHCommand(ctx),
      rotate_180: new Rotate180Command(ctx),
      duplicate: new DuplicateCommand(ctx),
      default_size: new DefaultSizeCommand(ctx),
    };
  }

  // ── executeAdminRadialAction (verbatim dispatch semantics) ──────────────────
  // Same behavior as the original if/else chain: run the matching command (if
  // any — an unrecognized actionKey is a silent no-op, exactly like falling
  // through every `else if`), then unconditionally close the radial menu.
  execute = (actionKey: string, mapObject: MapObject, marker: PIXI.Container): void => {
    this.commands[actionKey]?.execute(mapObject, marker);
    this.ctx.closeAdminRadialMenu();
  };
}
